import styles from "./SessionHeader.module.css";
import DurationInfoIcon from "../images/live/duration-info.svg?react";
import CapacityInfoIcon from "../images/live/capacity-info.svg?react";
import DateInfoIcon from "../images/live/date-info.svg?react";
import { SessionEntryProps } from "../edit-quiz-steps/LiveSessionStep";
import { Typography, ListItemText } from "@mui/material";
import { useState } from "react";
import { CoreTextField, CoreMenuButton, CoreMenuItem, CoreExpandButton, CoreRadioCardIcon } from "../../core";
import { useMobileLayout } from "../../hooks";
import i18nPackage from "../../i18n/i18n";
import {
	PendingLiveSession,
	isLiveSessionNameChanged,
	isLiveSessionDemoMode,
	MAX_DURATION,
	MAX_DURATION_DEMO,
	spansMultipleDays,
	formatDuration,
	formatShortDate,
	formatTime,
} from "./liveHelper";
import { ConditionalWrapper } from "../../wrapper";

type SessionHeaderControlRowProps = Pick<
	SessionHeaderProps,
	"allowDemoSessions" | "onChange" | "onDelete" | "expanded" | "setExpanded" | "existingSession" | "sessionIndex"
> & {
	session: PendingLiveSession;
};

function SessionHeaderControlRow({
	session,
	allowDemoSessions,
	onChange,
	onDelete,
	expanded,
	setExpanded,
	existingSession,
	sessionIndex,
}: SessionHeaderControlRowProps) {
	const { name, startTime, endTime } = session;
	const currTime = new Date();
	const isStarted = !!existingSession && new Date(existingSession.startTime) <= currTime;
	const isPast = existingSession && new Date(existingSession.endTime) <= currTime;

	const nameChanged = !existingSession || isLiveSessionNameChanged(existingSession, session);
	const nameError = nameChanged && !name;

	const demoMode = isLiveSessionDemoMode(session);
	const setDemoMode = (enabled: boolean) => {
		if (enabled && !demoMode) {
			onChange({
				...session,
				name: `${name.trim()} (Demo)`,
				endTime:
					Math.abs(endTime.getTime() - startTime.getTime()) <= MAX_DURATION
						? new Date(startTime.getTime() + MAX_DURATION_DEMO)
						: endTime,
			});
		} else if (demoMode) {
			onChange({
				...session,
				name: name.replace(/\s*\(Demo\)\s*$/, ""),
				endTime:
					Math.abs(endTime.getTime() - startTime.getTime()) > MAX_DURATION
						? new Date(startTime.getTime() + 60 * 60 * 1000)
						: endTime,
			});
		}
	};

	const [menuShowing, setMenuShowing] = useState(false);
	const isMobileLayout = useMobileLayout();
	return (
		<div className={styles.headerRow}>
			<ConditionalWrapper
				condition={!isMobileLayout}
				whenTrue={(children) => <div className={styles.nameWrapper}>{children}</div>}
			>
				{!expanded && (
					<div className={styles.sessionNumContainer}>
						<Typography>{sessionIndex + 1}</Typography>
					</div>
				)}
				{expanded ? (
					<CoreTextField
						className={styles.nameField}
						value={name}
						onChange={(e) => onChange({ ...session, name: e.target.value })}
						disabled={isStarted || isPast}
						disabledTooltip={
							isStarted ? i18nPackage.t("editQuizPage.liveSessionStep.header.sessionNameTooltip") : undefined
						}
						label={i18nPackage.t("editQuizPage.liveSessionStep.header.sessionNamePlaceholder")}
						error={nameError && i18nPackage.t("editQuizPage.liveSessionStep.header.emptySessionName")}
						fullWidth={isMobileLayout}
					/>
				) : (
					<Typography className={styles.sessionName}>{name}</Typography>
				)}
			</ConditionalWrapper>
			<div className={styles.sessionHeaderControls}>
				<CoreMenuButton
					menuShowing={menuShowing}
					setMenuShowing={setMenuShowing}
					disabled={isStarted}
					tooltip={i18nPackage.t("editQuizPage.liveSessionStep.header.actions")}
					disabledTooltip={i18nPackage.t("editQuizPage.liveSessionStep.header.actionsDisabled")}
				>
					<CoreMenuItem
						onClick={() => {
							onDelete();
							setMenuShowing(false);
						}}
					>
						<ListItemText primary={i18nPackage.t("editQuizPage.liveSessionStep.delete")} />
					</CoreMenuItem>
					{allowDemoSessions && (
						<CoreMenuItem
							onClick={() => {
								setDemoMode(!demoMode);
								setMenuShowing(false);
							}}
						>
							<ListItemText
								primary={
									demoMode
										? i18nPackage.t("editQuizPage.liveSessionStep.header.standard")
										: i18nPackage.t("editQuizPage.liveSessionStep.header.demo")
								}
							/>
						</CoreMenuItem>
					)}
				</CoreMenuButton>
				<CoreExpandButton
					expanded={expanded}
					onChange={setExpanded}
					disabled={isPast}
					disabledTooltip={i18nPackage.t("editQuizPage.liveSessionStep.expandDisabled")}
					collapsedTooltip={i18nPackage.t("editQuizPage.liveSessionStep.expand")}
					expandedTooltip={i18nPackage.t("editQuizPage.liveSessionStep.collapse")}
					className={styles.sessionExpandButton}
					iconVariant="narrow"
					slotProps={{
						icon: {
							viewBox: "0 0 20 20",
						},
					}}
				/>
			</div>
		</div>
	);
}

type SessionInfoCardProps = {
	icon: React.ReactNode;
	title: string;
	description: string;
};

function SessionInfoCard({ description, icon, title }: SessionInfoCardProps) {
	return (
		<div className={styles.sessionInfoCard}>
			<CoreRadioCardIcon icon={icon} enabled />
			<div className={styles.sessionInfoTextCol}>
				<Typography className={styles.title}>{title}</Typography>
				<Typography className={styles.description}>{description}</Typography>
			</div>
		</div>
	);
}

function SessionInfoHeader({ session }: { session: PendingLiveSession }) {
	const { capacity } = session;

	const multipleDates = spansMultipleDays(session.startTime, session.endTime);

	const startTime = formatTime(session.startTime);
	const endTime = formatTime(session.endTime);
	const startDate = formatShortDate(session.startTime);
	const endDate = formatShortDate(session.endTime);
	const durationMs = session.endTime.getTime() - session.startTime.getTime();

	return (
		<div className={styles.sessionDetailsRow}>
			<SessionInfoCard
				icon={<DateInfoIcon />}
				title={i18nPackage.t("editQuizPage.liveSessionStep.header.date")}
				description={
					multipleDates
						? i18nPackage.t("editQuizPage.liveSessionStep.header.multipleDates", {
								startTime,
								endTime,
								startDate,
								endDate,
							})
						: i18nPackage.t("editQuizPage.liveSessionStep.header.oneDay", { startDate, startTime, endTime })
				}
			/>
			<SessionInfoCard
				icon={<DurationInfoIcon />}
				title={i18nPackage.t("editQuizPage.liveSessionStep.header.duration")}
				description={formatDuration(durationMs, { style: "short" })}
			/>
			<SessionInfoCard
				icon={<CapacityInfoIcon />}
				title={i18nPackage.t("editQuizPage.liveSessionStep.header.capacity")}
				description={i18nPackage.t("editQuizPage.liveSessionStep.header.capacityLabel", { capacity })}
			/>
		</div>
	);
}

type SessionHeaderProps = Pick<
	SessionEntryProps,
	| "allowDemoSessions"
	| "expanded"
	| "setExpanded"
	| "onDelete"
	| "sessionIndex"
	| "existingSession"
	| "onChange"
	| "session"
>;

export default function SessionHeader({
	allowDemoSessions,
	expanded,
	onDelete,
	sessionIndex,
	setExpanded,
	existingSession,
	onChange,
	session,
}: SessionHeaderProps) {
	return (
		<div className={styles.sessionHeaderContainer}>
			<SessionHeaderControlRow
				allowDemoSessions={allowDemoSessions}
				expanded={expanded}
				setExpanded={setExpanded}
				onDelete={onDelete}
				onChange={onChange}
				session={session}
				existingSession={existingSession}
				sessionIndex={sessionIndex}
			/>
			{!expanded && <SessionInfoHeader session={session} />}
		</div>
	);
}
